import { ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, ControlValueAccessor } from '@angular/forms';
import { Field, Option } from 'ngx-angular-query-builder';

export interface NameQueryFieldValue {
  first_name: string;
  middle_name: string;
  last_name: string;
  nickname: string;
  suffix?: string;
  middle_name_blank: boolean;
  last_name_blank: boolean;
  nickname_blank: boolean;
  suffix_blank?: boolean;
}
@Component({
  selector: 'app-name-query-field-input',
  templateUrl: `./name-query-field-input.component.html`,
  styleUrls: ['./name-query-field-input.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NameQueryFieldInputComponent), multi: true }
  ]
})
export class NameQueryFieldInputComponent
  implements ControlValueAccessor {

  /** Field configuration */
  @Input() field: Field;
  @Input() options: Option[];

  /** Disabled state of the input */
  @Input() disabled: boolean;

  // For ControlValueAccessor interface
  public onChangeCallback: () => void;
  public onTouchedCallback: () => any;

  firstName: string = null;
  middleName: string = null;
  lastName: string = null;
  nickname: string = null;
  suffix: string = null;

  /** begin: AB-676 mockup variables */
  fieldNameLabels = {
    firstName: 'First Name',
    middleName: 'Middle Name',
    lastName: 'Last Name',
    nickname: 'Preferred Name',
    suffix: 'Suffix'
  };

  middleNameNullChecked = false;
  lastNameNullChecked = false;
  nicknameNullChecked = false;
  suffixNullChecked = false;
  /** end: AB-676 mockup variables */

  @Input()
  get value(): NameQueryFieldValue {
    return {
      first_name: this.firstName,
      middle_name: this.middleNameNullChecked ? null : this.middleName,
      last_name: this.lastNameNullChecked ? null : this.lastName,
      nickname: this.nicknameNullChecked? null: this.nickname,
      suffix: this.suffixNullChecked? null: this.suffix,
      middle_name_blank: this.middleNameNullChecked,
      last_name_blank: this.lastNameNullChecked,
      nickname_blank: this.nicknameNullChecked,
      suffix_blank: this.suffixNullChecked,
    };
  }
  set value(value: NameQueryFieldValue) {
    const ruleValue = value;

    this.middleNameNullChecked = !!ruleValue?.middle_name_blank;
    this.lastNameNullChecked = !!ruleValue?.last_name_blank;
    this.nicknameNullChecked = !!ruleValue?.nickname_blank;
    this.suffixNullChecked = !!ruleValue?.suffix_blank;

    this.firstName = ruleValue?.first_name;
    this.middleName = this.middleNameNullChecked ? null : ruleValue?.middle_name;
    this.lastName = this.lastNameNullChecked ? null : ruleValue?.last_name;
    this.nickname = this.nicknameNullChecked ? null :ruleValue?.nickname;
    this.suffix = this.suffixNullChecked ? null :ruleValue?.suffix;
    this.handleDataChange();
  }

  constructor(
    private _cd: ChangeDetectorRef
  ) {
  }

  modelChanged(property, newValue) {
    this[property] = newValue;
    this.handleDataChange();
  }

  nameNullTooltipText(fieldName: string) {
    return 'Check this box to search for entities that do NOT have an entry for “' + fieldName + '”';
  }

  getNameLabel(fieldName: string, isChecked: boolean) {
    return (isChecked) ? this.fieldNameLabels[fieldName] + ' is null' : this.fieldNameLabels[fieldName];
  }

  onNullNameCheckboxChanged(isChecked: boolean, fieldName: string) {
    const labelSuffix = ' is blank';
    let labelText = this.fieldNameLabels[fieldName];
    if(isChecked) {
      this.modelChanged(fieldName, null);
      labelText = (labelText.endsWith(labelSuffix)) ? labelText : labelText + labelSuffix;
    } else {
      this.fieldNameLabels[fieldName];
      labelText = (labelText.endsWith(labelSuffix)) ? labelText.split(labelSuffix)[0] : labelText;
    }
    this.fieldNameLabels[fieldName] = labelText;
  }

  // ----------ControlValueAccessor Implementation----------
  writeValue(obj: any): void {
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = () => fn(this.value);
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = () => fn(this.value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cd.detectChanges();
  }

  private handleDataChange(): void {
    this._cd.markForCheck();
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }

}
